/** **TEST DRIVE — a drafter times the app themselves, one instruction at a time.**
 *
 * ⚠⚠⚠ **EVERY NUMBER THIS PROJECT HAS AGAINST BricsCAD CAME FROM A ROBOT ON THE BOX THAT SERVES
 * THE APP.** `qa/bench_actions.py` types at a fixed delay, clicks exact coordinates, never
 * hesitates, and shares a machine with Postgres and the engine it is measuring. A designer sits
 * at a different PC, over the LAN, with their own file — and nothing in this repo has ever
 * measured that. This does: the app shows one step, times it with the SAME marks the benchmarks
 * read (`__pdcadEdits`, `__pdcadOptimistic`, `__pdcadScene`, `__pdcadRender`), and posts the
 * result as it goes.
 *
 * ⚠⚠ **THE COMMENT IS HALF THE POINT.** A number says an action took 180 ms; only the person
 * doing it can say it felt like a hitch, that the object landed in the wrong place, or that they
 * had to do it twice. Every step carries both, and a "felt slow" tick that is independent of the
 * clock — because the two disagreeing is the most interesting result this can produce.
 *
 * ⚠⚠ **A STEP THAT WAS SKIPPED IS POSTED AS SKIPPED.** Half of 2026-09-20 went on benchmarks
 * that silently dropped the cases they could not run and reported the rest as a clean sweep. A
 * run covering 12 of 30 steps has to say 12 of 30.
 *
 * ⚠ Posted per STEP, not at the end: a run abandoned half way — the drafter is called away, the
 * tab is closed, the step they were on is the one that hung — is exactly the run worth reading.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** One instruction. `edit` means the step ends when the drawing changes; the others end when the
 *  drafter says so, because panning and opening commit nothing. */
interface Step {
  key: string;
  label: string;
  /** What to do, in the drafter's words. ⚠ No command names where a gesture will do: "drag the
   *  drawing around with the middle button" is testable by someone who has never read a manual. */
  how: string;
  kind: "edit" | "view" | "open";
  /** Shown under the instruction when there is something worth warning about. */
  hint?: string;
}

/** ⚠⚠ **THE ORDER IS A DRAFTER'S MORNING, NOT A TEST PLAN.** Open, look around, draw a few
 *  things, change them, undo. A run that front-loads the exotic commands gets abandoned before
 *  it reaches the ones everybody uses.
 *  ⚠ The view steps come FIRST and are asked for twice — once on a cold drawing and once after
 *  editing — because `qa/bench_actions.py` measures panning on an idle canvas and reports the
 *  60 Hz cap, which says nothing about how it feels while the app is busy. */
const STEPS: Step[] = [
  { key: "open", kind: "open", label: "Open the drawing",
    how: "Open the file you want to test, and press Done the moment you can see the drawing.",
    hint: "If it takes long enough to annoy you, say so in the comment — that is the finding." },
  { key: "zoom-extents", kind: "view", label: "Zoom to the whole drawing",
    how: "Double-click the middle mouse button, or type ZOOM then E." },
  { key: "pan", kind: "view", label: "Pan around",
    how: "Hold the middle mouse button and drag the drawing about for a few seconds.",
    hint: "Move continuously — the point is whether it keeps up, not how fast one jump is." },
  { key: "zoom-wheel", kind: "view", label: "Zoom in and out with the wheel",
    how: "Scroll the wheel in and out over a busy part of the drawing, for a few seconds." },
  { key: "pick", kind: "view", label: "Click one object",
    how: "Click a single line or wall so it highlights." },
  { key: "window", kind: "view", label: "Window-select a crowded area",
    how: "Drag a selection window over a busy part of the drawing." },
  { key: "line", kind: "edit", label: "Draw a line",
    how: "Type LINE, click two points, then press Escape." },
  { key: "pline", kind: "edit", label: "Draw a polyline",
    how: "Type PLINE, click three or four points, then press Enter." },
  { key: "circle", kind: "edit", label: "Draw a circle",
    how: "Type CIRCLE, click the centre, then click to set the radius." },
  { key: "text", kind: "edit", label: "Write some text",
    how: "Type TEXT, click where it goes, accept the height, and type a word." },
  { key: "dim", kind: "edit", label: "Add a dimension",
    how: "Type DIMLINEAR and pick two points and a place for the line." },
  { key: "move", kind: "edit", label: "Move what you just drew",
    how: "Select a few of the objects you made, type MOVE, and drag them somewhere." },
  { key: "copy", kind: "edit", label: "Copy something",
    how: "Select an object, type COPY, and place a copy beside it." },
  { key: "rotate", kind: "edit", label: "Rotate something",
    how: "Select an object, type ROTATE, pick a base point and turn it." },
  { key: "trim", kind: "edit", label: "Trim a line",
    how: "Draw two crossing lines, type TRIM, and click the stub you want gone." },
  { key: "erase", kind: "edit", label: "Erase something",
    how: "Select an object and press Delete." },
  { key: "undo", kind: "edit", label: "Undo",
    how: "Press Ctrl+Z once." },
  { key: "layer-off", kind: "view", label: "Turn a layer off and on",
    how: "Open the layer list and toggle a busy layer off, then on again.",
    hint: "This is the one a drafter does dozens of times an hour while reading a drawing." },
  { key: "pan-after", kind: "view", label: "Pan again, now the drawing has been edited",
    how: "Drag the drawing about again for a few seconds.",
    hint: "Same gesture as before. If it is worse than the first time, that is worth knowing." },
  { key: "save", kind: "view", label: "Save",
    how: "Save the drawing, and press Done when it says it is saved." },
  { key: "reopen", kind: "open", label: "Close and open it again",
    how: "Go back to the file list, open the same drawing again, and press Done when you see it.",
    hint: "A known problem lives here: after an edit this can take several seconds." },
];

/** ⚠ Typed as a plain string map, not a union of two object shapes: spread into `headers`, the
 *  `{}` branch narrows to `undefined` and TypeScript refuses the whole `HeadersInit`. */
const csrf = (): Record<string, string> => {
  const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
  return m ? { "X-CSRFToken": decodeURIComponent(m[1]) } : {};
};

async function post(url: string, body: unknown) {
  const r = await fetch(url, {
    method: "POST", credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json", ...csrf() },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
  return r.json();
}

/** The marks the app publishes, snapshotted. ⚠ Read rather than recomputed: these are the same
 *  numbers `qa/bench_actions.py` reads, so a run here and a run there can be set side by side. */
function marksNow(): Record<string, unknown> {
  const w = window as unknown as Record<string, any>;
  const last = (w.__pdcadEdits || []).slice(-1)[0];
  return {
    edits: (w.__pdcadEditCount as number) ?? (w.__pdcadEdits || []).length,
    lastEditMs: last?.ms ?? null,
    lastOp: last?.op ?? null,
    optimistic: w.__pdcadOptimistic ? { ...w.__pdcadOptimistic } : null,
    scene: w.__pdcadScene
      ? { cuts: w.__pdcadScene.cuts, lines: w.__pdcadScene.lines,
          fills: w.__pdcadScene.fills, build: w.__pdcadScene.build,
          reuse: w.__pdcadScene.bandReuse, patched: w.__pdcadScene.patched }
      : null,
    render: w.__pdcadRender ? { ...w.__pdcadRender } : null,
  };
}

export default function AcadTestDrive(
  { open, close, planId, who }:
  { open: boolean; close: () => void; planId?: number | null; who?: string },
) {
  //: ⚠ Asked for, because every run so far is filed under "someone" — and a number from a
  //: machine and a person nobody named cannot be compared with the next one.
  const [name, setName] = useState(who || "");
  const [started, setStarted] = useState(false);
  const [runId, setRunId] = useState<number | null>(null);
  const [at, setAt] = useState(0);
  const [running, setRunning] = useState(false);
  const [comment, setComment] = useState("");
  const [slow, setSlow] = useState(false);
  const [sent, setSent] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");
  //: ⚠⚠⚠ **THE CLOCK STARTS ON THE FIRST THING THEY DO, NOT ON THE START BUTTON.** The first
  //: run of this recorded `open 19,024 ms` and `pan 15,071` — fifteen to nineteen SECONDS, which
  //: is a person reading an instruction and reaching for the mouse, not an app. A number that
  //: large is not merely imprecise, it drowns the thing being measured. The first mousedown,
  //: key, or wheel after Start is when the step actually begins.
  //: ⚠ `null` until then, so a step they never began records no time rather than a fictitious one.
  const t0 = useRef<number | null>(null);
  const [armed, setArmed] = useState(false);
  const before = useRef<Record<string, unknown>>({});
  //: Frame intervals sampled while a step is live — the only honest way to say whether PANNING
  //: kept up. ⚠ A median and a worst, not a mean: one 300 ms stall is what a drafter notices and
  //: a mean of sixty frames hides it completely.
  const frames = useRef<number[]>([]);
  const raf = useRef(0);

  const step = STEPS[at];

  useEffect(() => {
    if (!open || runId || !started) return;
    (async () => {
      try {
        const w = window as unknown as Record<string, any>;
        const gl = w.__pdcadGL || {};
        const r = await post("/api/testruns/", {
          drawing: planId ?? null,
          who: name.trim() || "unnamed",
          agent: {
            ua: navigator.userAgent,
            screen: `${window.screen.width}x${window.screen.height}`,
            dpr: window.devicePixelRatio,
            gpu: gl.kind ?? null, renderer: gl.renderer ?? null,
            open: w.__pdcadOpen ? { ...w.__pdcadOpen, counts: undefined } : null,
          },
        });
        setRunId(r.id);
      } catch (e) { setErr(String(e)); }
    })();
  }, [open, runId, planId, name, started]);

  const sample = useCallback(() => {
    let last = performance.now();
    const tick = () => {
      const now = performance.now();
      frames.current.push(now - last);
      last = now;
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
  }, []);

  const start = useCallback(() => {
    frames.current = [];
    before.current = marksNow();
    t0.current = null;
    setComment(""); setSlow(false); setRunning(true); setArmed(true);
    sample();
  }, [sample]);

  //: The first real interaction after Start — see `t0`. ⚠ Capture phase and `once`-like guard, so
  //: it cannot miss an event the canvas stops, and cannot restart the clock mid-step.
  useEffect(() => {
    if (!armed) return;
    const go = () => { if (t0.current == null) t0.current = performance.now(); };
    for (const e of ["mousedown", "keydown", "wheel"]) {
      window.addEventListener(e, go, true);
    }
    return () => {
      for (const e of ["mousedown", "keydown", "wheel"]) {
        window.removeEventListener(e, go, true);
      }
    };
  }, [armed]);

  const stop = useCallback(async (outcome: "done" | "skipped") => {
    cancelAnimationFrame(raf.current);
    setArmed(false);
    // ⚠ `null` when they pressed Done without touching anything — a step with no interaction has
    // no duration, and inventing one is the "fast number for work not done" this repo has shipped.
    const wall = running && t0.current != null ? performance.now() - t0.current : null;
    const now = marksNow();
    const grew = (now.edits as number) - ((before.current.edits as number) ?? 0);
    const f = frames.current.slice().sort((a, b) => a - b);
    // ⚠⚠ **THE EDIT'S OWN MARK WHEN THERE WAS ONE, THE WALL CLOCK OTHERWISE — AND `null` WHEN
    // NOTHING HAPPENED.** The wall clock here includes the drafter reading the instruction and
    // reaching for the mouse, which is not the app's time; `lastEditMs` is the app's own measure
    // of the command. A step that committed nothing and was not a view step is worth no number
    // at all, and giving it one is the "fast number for work not done" this repo has shipped.
    const ms = outcome === "skipped" ? null
      : step.kind === "edit" ? (grew > 0 ? (now.lastEditMs as number) : null)
      : wall;
    try {
      if (runId) {
        await post(`/api/testruns/${runId}/step/`, {
          key: step.key, label: step.label, ms, outcome,
          comment, slow,
          marks: {
            // ⚠ INSIDE `marks`, because that is the only free-form field the endpoint keeps —
            // a top-level key is dropped, and the report then fell back to counting the steps it
            // had and printed "1 of 1" for a run one step into twenty-one.
            of: STEPS.length, at: at + 1,
            kind: step.kind, wall, editsCommitted: grew,
            frames: f.length
              ? { n: f.length, median: Math.round(f[f.length >> 1] * 10) / 10,
                  worst: Math.round(f[f.length - 1] * 10) / 10 }
              : null,
            after: now,
          },
        });
      }
      setSent((s) => ({ ...s, [step.key]: outcome }));
      setErr("");
    } catch (e) { setErr(String(e)); }
    setRunning(false);
    setComment(""); setSlow(false);
    if (at + 1 < STEPS.length) setAt(at + 1);
  }, [at, comment, runId, running, slow, step]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);

  if (!open) return null;
  const doneN = Object.values(sent).filter((v) => v === "done").length;
  const skipN = Object.values(sent).filter((v) => v === "skipped").length;

  return (
    <div style={{ position: "fixed", right: 16, bottom: 16, width: 330, zIndex: 80,
                  background: "rgba(28,30,34,.97)", border: "1px solid #6d6d6d",
                  borderRadius: 4, color: "#e8e8e8", font: "12px system-ui",
                  boxShadow: "0 6px 24px rgba(0,0,0,.5)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px",
                    borderBottom: "1px solid #444" }}>
        <b style={{ flex: 1 }}>Test drive</b>
        <span style={{ color: "#9aa0a6" }}>
          {at + 1} / {STEPS.length} · {doneN} done{skipN ? `, ${skipN} skipped` : ""}
        </span>
        <button onClick={close} style={btn}>Close</button>
      </div>
      {err && <div style={{ padding: "6px 10px", color: "#e88" }}>{err}</div>}
      {!started && (
        <div style={{ padding: "10px" }}>
          <div style={{ color: "#cfd3d7", marginBottom: 6 }}>
            Who is driving? The run is filed under this name.
          </div>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="your name"
                 style={{ width: "100%", background: "#1e1e1e", border: "1px solid #555",
                          color: "#e8e8e8", font: "12px system-ui", padding: "3px 5px",
                          boxSizing: "border-box" }} />
          <button onClick={() => setStarted(true)} disabled={!name.trim()}
                  style={{ ...btn, marginTop: 8, width: "100%",
                           background: name.trim() ? "#2d5f2d" : "#3a3d42" }}>
            Begin the run
          </button>
          <div style={{ color: "#9aa0a6", marginTop: 8, fontSize: 11, lineHeight: 1.45 }}>
            {STEPS.length} steps. Press Start on each, do it, press Done. Skip anything that does
            not apply — a skipped step is recorded as skipped, not as a pass.
          </div>
        </div>
      )}
      {started && <div style={{ padding: "10px 10px 6px" }}>
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{step.label}</div>
        <div style={{ color: "#cfd3d7", lineHeight: 1.45 }}>{step.how}</div>
        {step.hint && (
          <div style={{ color: "#9aa0a6", marginTop: 5, fontSize: 11.5 }}>⚠ {step.hint}</div>
        )}
      </div>}
      {started && <div style={{ padding: "0 10px 8px" }}>
        <textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  placeholder="How did it feel? Anything wrong?"
                  style={{ width: "100%", height: 44, background: "#1e1e1e",
                           border: "1px solid #555", color: "#e8e8e8", font: "11.5px system-ui",
                           padding: 4, resize: "vertical", boxSizing: "border-box" }} />
        <label style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5,
                        color: "#cfd3d7" }}>
          <input type="checkbox" checked={slow} onChange={(e) => setSlow(e.target.checked)} />
          that felt slow
        </label>
      </div>}
      {started && <div style={{ display: "flex", gap: 6, padding: "0 10px 10px" }}>
        {!running
          ? <button onClick={start} style={{ ...btn, flex: 1, background: "#2d5f2d" }}>
              Start this step
            </button>
          : <button onClick={() => void stop("done")}
                    style={{ ...btn, flex: 1, background: "#2d5f2d" }}>Done</button>}
        <button onClick={() => void stop("skipped")} style={btn}>Skip</button>
        <button onClick={() => { setAt(Math.max(0, at - 1)); setRunning(false); }} style={btn}>
          Back
        </button>
      </div>}
      {started && (
        <div style={{ padding: "0 10px 9px", color: "#9aa0a6", fontSize: 11 }}>
          {running
            ? "timing from your first click or keystroke — then press Done"
            : "press Start, then do it. The clock begins when you touch the drawing."}
        </div>
      )}
    </div>
  );
}

const btn: React.CSSProperties = {
  background: "#3a3d42", border: "1px solid #6d6d6d", color: "#e8e8e8",
  font: "11.5px system-ui", padding: "3px 9px", borderRadius: 3, cursor: "pointer",
};
